import { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  TextField,
  Alert,
  CircularProgress,
  Link,
  useTheme
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@shared/services/ApiClient'
import { ArrowBack, CheckCircle } from '@mui/icons-material'

const forgotPasswordSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address')
})

interface ForgotPasswordFormData {
  email: string
}

function ForgotPasswordPage() {
  const theme = useTheme()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(forgotPasswordSchema),
    mode: 'onBlur'
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      await apiClient.post('/auth/reset-password', {
        Email: data.email
      })

      setSuccess(true)
    } catch (err: any) {
      // Even on error, show success message for security (don't reveal if email exists)
      // But if there's a server error, show it
      if (err.message?.includes('internal') || err.message?.includes('error')) {
        setError('Something went wrong. Please try again later.')
      } else {
        setSuccess(true)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Container maxWidth="sm">
        <Paper sx={{ p: 6, borderRadius: 2 }}>
          {!success ? (
            <>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Forgot Password?
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Enter your email address and we'll send you a link to reset your password
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit(onSubmit)}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isLoading}
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    mb: 3
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  onClick={() => navigate('/login')}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  <ArrowBack sx={{ mr: 0.5, fontSize: '1rem' }} />
                  Back to Sign In
                </Link>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                Check Your Email
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                If an account exists with that email address, we've sent you a password reset link.
                Please check your inbox and follow the instructions.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Didn't receive the email? Check your spam folder or try again in a few minutes.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
                sx={{ mr: 2 }}
              >
                Back to Sign In
              </Button>
              <Button
                variant="text"
                onClick={() => setSuccess(false)}
              >
                Try Another Email
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  )
}

export default ForgotPasswordPage
